/**
 * The build shop: ten towers, each wearing its catalog icon.
 *
 * The catalog arrives through `setCatalog`, injected by `main.ts`. This panel
 * never imports the tower subsystem — it only knows the shape below, which a
 * `TowerArchetype` happens to satisfy.
 *
 * Placement works two ways, because both feel right to different hands: click
 * an item to arm it and click the playfield, or drag the item onto the
 * playfield and release. The drag ghost is the tower's own icon at build scale.
 *
 * Exactly one drag starts in here now, and it is the one that builds. The panel
 * used to carry a second: a BUILD title row that was a grab bar for the plate
 * itself, told apart from a tile drag by where the finger went down and by
 * nothing else. That plate does not move any more — it is a bar floating over
 * the board, sized to its content and parked where the corridor is thinnest —
 * so the header, its grab mark, its fold chevron and its dock control have all
 * gone, and with them the only gesture in the cabinet that meant two things.
 */

import type { PhosphorName } from '@/core/palette';
import {
  el, icon, iconFromId, installUiTheme, money, phosphorVar, safeIconId,
} from './Icon';
import type { IconName } from './Icon';

/** The minimum a tower must expose to appear in the shop. */
export interface ShopEntry {
  readonly id: string;
  readonly name: string;
  /** Icon id from the vendored set. Validated when the row is built. */
  readonly icon: string;
  readonly colour: PhosphorName;
  readonly cost: number;
  readonly blurb: string;
}

export interface ShopState {
  cash: number;
  /** Id of the tower armed for placement, or null. */
  selected: string | null;
  /**
   * Ids the act schedule has not unlocked yet. They are *removed* from the
   * shelf, not greyed on it.
   *
   * This used to dim them and explain, in a tooltip, which act they arrive in.
   * That is seven disabled controls on the busiest screen a new player sees,
   * and it spends the reveal before the reveal: a Singularity the player has
   * been looking at for forty rounds is a purchase, not a moment. Absent, the
   * shelf grows — and `syncShelf` animates what arrives.
   */
  locked?: ReadonlySet<string>;
  /**
   * True while the tower detail is the panel's current view.
   *
   * BUILD is the root of a two-deep view stack and the detail is pushed over
   * it, so the shelf is not merely occluded — it is off the surface, and
   * `ui.css` takes the whole rail out of the layout while this holds. `inert`
   * still goes on, because it covers the frames either side of that: the push
   * animation, and any layout where a stylesheet override leaves the plate up.
   */
  covered?: boolean;
}

export interface ShopCallbacks {
  /** Arm or disarm placement mode for a tower. */
  onSelect?(id: string | null): void;
  /**
   * A drag is in flight over the playfield. Coordinates are client-space.
   *
   * Without this the validity overlay only learned where the tower was going at
   * the instant it was already built, which on a phone — where the drag *is*
   * the aiming gesture, because there is no hover to aim with — meant the
   * green/red read arrived one frame too late to act on.
   */
  onDrag?(id: string, clientX: number, clientY: number): void;
  /** A drag ended over the playfield. Coordinates are client-space. */
  onPlace?(id: string, clientX: number, clientY: number): void;
  /** Hover or focus moved onto an entry, so the range preview can follow. */
  onPreview?(entry: ShopEntry | null): void;
}

/** Fallback when a catalog entry names an icon that is not in the vendored set. */
const FALLBACK_ICON: IconName = 'shapes';

const DRAG_THRESHOLD_PX = 5;

/**
 * How far above the fingertip a touch drag aims, in CSS pixels.
 *
 * The ghost used to sit exactly under the contact point, which on a phone means
 * under the pad of a thumb: measured, the ghost's centre matched the touch point
 * to within 0.1px, a 32px icon inside a 45px contact patch, over a tile 9px
 * wide. The player could see the range ring — it is bigger than a hand — and
 * could not see the one thing the gesture is aiming, which is the tile.
 *
 * The *aim* moves with the art, not just the picture of it. Handing `onDrag`
 * the raw `clientY` while drawing the ghost 28px higher would be worse than
 * either: the overlay would go green for a tile the icon is not standing on.
 * `main.ts` cancels a release outside the canvas, so a lift near the top edge
 * fails safe rather than clamping to a border tile.
 *
 * Zero for a mouse. A cursor is a point, it is not standing on anything, and a
 * ghost that floated 28px above it would read as a bug.
 */
const TOUCH_LIFT_PX = 28;

/**
 * The hint has to name the gesture the player actually has. "Click to arm" on a
 * phone is instructions for a device that is not in their hand, and the
 * hold-for-detail line only exists where hover does not.
 */
const TOUCH = typeof matchMedia === 'function' && matchMedia('(hover: none)').matches;
const HINT_IDLE = TOUCH
  ? 'Tap to arm, drag to place, hold for detail.'
  : 'Click to arm, or drag onto the field.';
const HINT_ARMED = TOUCH
  ? 'Drag on the field, release to build.'
  : 'Click the field to place. [Esc] cancels.';

interface Row {
  entry: ShopEntry;
  node: HTMLButtonElement;
  costNode: HTMLElement;
  iconNode: HTMLElement;
}

export class ShopPanel {
  private root = el('div', 'panel shop');
  private tray = el('div', 'shop__tray');
  private grid = el('div', 'shop__grid');
  private moreStart = el('div', 'shop__more shop__more--start');
  private moreEnd = el('div', 'shop__more shop__more--end');
  private trayObserver: ResizeObserver | null = null;
  private hint = el('p', 'shop__hint', HINT_IDLE);
  private rows: Row[] = [];
  private catalog: readonly ShopEntry[] = [];
  /** Ids on the shelf right now, in render order. */
  private shown: string[] = [];
  /** True once a shelf has been rendered, so the first one does not animate. */
  private shelved = false;
  private revealTimer = 0;
  private state: ShopState = { cash: 0, selected: null };
  /** Whether the detail view was pushed last frame, so a pop can be animated. */
  private wasCovered = false;
  private returnTimer = 0;
  private mounted = false;

  private ghost: HTMLElement | null = null;
  private dragging: Row | null = null;
  private dragFrom = { x: 0, y: 0 };
  private dragPointer = -1;
  private dragArmed = false;
  private dragArmedTimer = 0;
  /** CSS pixels the live drag aims above the contact point. Zero for a mouse. */
  private lift = 0;

  constructor(private readonly cb: ShopCallbacks = {}) {}

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();

    /*
     * No header. The bar has no title, no grab mark, no fold chevron and no
     * dock control, because it has nothing left for any of them to do: it does
     * not move, it does not change edges, and the row of towers under this
     * comment is what tells a player the bar is the shop. The word `BUILD` over
     * ten labelled tower tiles was a caption on a photograph of itself, and the
     * 12px chevron beside it was the only way back from a panel folded to that
     * caption — which is the defect this whole change came from.
     */
    this.grid.setAttribute('role', 'listbox');
    this.grid.setAttribute('aria-label', 'Towers');

    /*
     * The shelf holds ten towers and a phone shows four of them. It has scrolled
     * since the touch-action fix, and it scrolled silently: the only signal that
     * six more existed was a word cut in half at the edge, which reads as a
     * layout bug rather than an invitation. Four towers the late acts are
     * balanced around were, for a player who never guessed to swipe, not in the
     * game.
     *
     * So the edge says so. A veil the tiles bleed under, an arrow pointing the
     * way the thumb should go, and both of them only on the side that has
     * something left to show — an arrow that is always lit is furniture.
     *
     * They live in a wrapper rather than in the scroller because a child of a
     * scroll container scrolls away with the content, and an affordance that
     * leaves with the thing it is pointing at is worse than none.
     */
    for (const [marker, x, y] of [
      [this.moreStart, 'angle-left', 'angle-up'],
      [this.moreEnd, 'angle-right', 'angle-down'],
    ] as const) {
      marker.setAttribute('aria-hidden', 'true');
      marker.append(
        icon(x, { size: 24, role: 'uiAccent', glow: 1, className: 'shop__more-x' }),
        icon(y, { size: 24, role: 'uiAccent', glow: 1, className: 'shop__more-y' }),
      );
    }

    this.tray.append(this.grid, this.moreStart, this.moreEnd);
    this.grid.addEventListener('scroll', this.syncOverflow, { passive: true });
    if (typeof ResizeObserver === 'function') {
      this.trayObserver = new ResizeObserver(this.syncOverflow);
      this.trayObserver.observe(this.grid);
    }

    this.root.append(this.tray, this.hint);
    root.append(this.root);
    this.update(this.state);
    this.syncOverflow();
  }

  /**
   * Which way the shelf still has towers, and along which axis.
   *
   * The axis is measured, not assumed. The dock lays the tray out sideways and
   * the landscape rail lays it out downwards, and the marker has to point the
   * way the thumb actually travels — an arrow aimed at an axis that does not
   * move is a lie told confidently. Whichever axis has the most left to give is
   * the one the tray is scrolling on; a rounding pixel is not overflow, hence
   * the 2px deadband at both ends.
   */
  private readonly syncOverflow = (): void => {
    const g = this.grid;
    const spanX = g.scrollWidth - g.clientWidth;
    const spanY = g.scrollHeight - g.clientHeight;
    const x = spanX >= spanY;
    const span = x ? spanX : spanY;
    const at = x ? g.scrollLeft : g.scrollTop;
    this.tray.dataset.axis = x ? 'x' : 'y';
    this.tray.classList.toggle('has-more-start', span > 2 && at > 2);
    this.tray.classList.toggle('has-more-end', span > 2 && at < span - 2);
  };

  /** Injected by `main.ts` once the tower subsystem has loaded its catalog. */
  setCatalog(entries: readonly ShopEntry[]): void {
    this.catalog = entries;
    if (this.mounted) this.update(this.state);
  }

  /**
   * Ids the shop is showing, in order, for whoever binds the number keys.
   *
   * Hotkey 4 has to mean the fourth tile a player can see. Indexing the full
   * catalogue armed a tower that is not on the shelf and, worse, told them when
   * it would be — which is the same leak the greyed tiles were.
   */
  get visibleIds(): readonly string[] {
    return this.shown;
  }

  update(state: ShopState): void {
    this.state = state;
    if (!this.mounted) return;

    const covered = state.covered ?? false;
    this.root.inert = covered;
    /*
     * Popping back to BUILD animates the shelf in, so the return reads as this
     * surface coming forward rather than as a second window closing. It is the
     * counterpart of the push in `.towerpanel`, mirrored, and it is the only
     * thing either panel does to say the two are one box.
     */
    if (this.wasCovered && !covered) {
      this.root.classList.remove('is-returning');
      void this.root.offsetWidth;
      this.root.classList.add('is-returning');
      window.clearTimeout(this.returnTimer);
      this.returnTimer = window.setTimeout(this.endReturn, 240);
    }
    this.wasCovered = covered;
    this.syncShelf();

    for (const row of this.rows) {
      const poor = row.entry.cost > state.cash;
      row.node.classList.toggle('is-locked', poor);
      row.node.classList.toggle('is-selected', state.selected === row.entry.id);
      row.node.setAttribute('aria-selected', String(state.selected === row.entry.id));
      row.node.disabled = false; // still focusable, so the tooltip explains why.
      row.iconNode.style.setProperty('--pxi-glow', poor ? '0' : '0.8');
      row.iconNode.style.setProperty(
        '--pxi-col',
        poor ? 'var(--ui-disabled)' : phosphorVar(row.entry.colour),
      );
      row.costNode.textContent = money(row.entry.cost);
      row.node.dataset.tip = `${row.entry.name} — ${row.entry.blurb}`;
    }

    this.hint.textContent = state.selected ? HINT_ARMED : HINT_IDLE;
  }

  /**
   * Brings the shelf into line with what the act schedule allows, and animates
   * whatever just arrived.
   *
   * A locked tower is *absent*, not dimmed. Seven greyed tiles at round one are
   * noise on the busiest screen a new player sees, and they spend the reveal
   * the act structure exists to deliver: the player already knows there is a
   * Singularity, so unlocking it is an accounting event rather than a moment.
   *
   * The first shelf is never animated. It is not a reveal, it is the game
   * starting, and a wipe on load reads as a loading artifact.
   */
  private syncShelf(): void {
    const locked = this.state.locked;

    // Allocation-free dirty check first: this runs every frame from update(),
    // and the filter/slice below built a fresh array (plus a closure) per
    // frame for a shelf that changes once an act. Walk the catalog against
    // what is shown; only build the real array when something moved.
    let same = true;
    let visible = 0;
    for (const e of this.catalog) {
      if (locked?.has(e.id)) continue;
      if (this.shown[visible] !== e.id) { same = false; break; }
      visible++;
    }
    if (same && visible !== this.shown.length) same = false;
    if (same && this.shelved) return;

    const next = locked
      ? this.catalog.filter((e) => !locked.has(e.id))
      : this.catalog.slice();

    same = next.length === this.shown.length;
    if (same) for (let i = 0; i < next.length; i++) {
      if (next[i].id !== this.shown[i]) { same = false; break; }
    }
    if (same && this.shelved) return;

    const known = new Set(this.shown);
    const fresh = this.shelved ? next.filter((e) => !known.has(e.id)).map((e) => e.id) : [];
    this.shown = next.map((e) => e.id);
    this.shelved = true;
    this.rebuild(next, new Set(fresh));
  }

  destroy(): void {
    window.clearTimeout(this.revealTimer);
    window.clearTimeout(this.returnTimer);
    window.clearTimeout(this.dragArmedTimer);
    this.grid.removeEventListener('scroll', this.syncOverflow);
    this.trayObserver?.disconnect();
    this.trayObserver = null;
    this.clearDrag();
    for (const row of this.rows) this.unbind(row);
    this.rows.length = 0;
    this.root.remove();
    this.mounted = false;
  }

  // ─────────────────────────────────────────────────────────────── internals ──

  private rebuild(entries: readonly ShopEntry[], fresh: ReadonlySet<string>): void {
    for (const row of this.rows) this.unbind(row);
    this.rows.length = 0;
    this.grid.replaceChildren();
    let revealIndex = 0;

    for (const entry of entries) {
      const node = el('button', 'shop__item');
      node.type = 'button';
      node.setAttribute('role', 'option');
      node.setAttribute('aria-selected', 'false');
      node.style.setProperty('--item-col', phosphorVar(entry.colour));
      node.dataset.towerId = entry.id;
      node.dataset.tip = `${entry.name} — ${entry.blurb}`;
      node.dataset.tipIcon = safeIconId(entry.icon, FALLBACK_ICON);
      node.dataset.tipCost = String(entry.cost);

      const iconNode = iconFromId(entry.icon, FALLBACK_ICON, {
        size: 24,
        phosphor: entry.colour,
        glow: 0.8,
      });
      const name = el('span', 'shop__name', entry.name);
      const cost = el('span', 'shop__cost');
      const costText = el('span', 'num', money(entry.cost));
      cost.append(icon('coin', { size: 12, role: 'uiCash' }), costText);

      node.append(iconNode, name, cost);
      node.setAttribute('aria-label', `${entry.name}, ${entry.cost} cash`);

      /*
       * The reveal: a scanline wipe down the tile, a phosphor charge that
       * settles out of overbright, and a stagger so five towers arrive as a
       * sequence rather than as a flash. All of it in `ui.css`, all of it
       * collapsed by the reduced-motion block at the foot of that file — which
       * is why this sets a class and an index and nothing else.
       */
      if (fresh.has(entry.id)) {
        node.classList.add('is-revealing');
        node.style.setProperty('--reveal-i', String(revealIndex++));
      }

      const row: Row = { entry, node, costNode: costText, iconNode };
      this.bind(row);
      this.rows.push(row);
      this.grid.append(node);
    }

    // The class is scaffolding. Left on, its `filter` keeps a stacking context
    // alive under every tile for the rest of the run.
    if (revealIndex > 0) {
      window.clearTimeout(this.revealTimer);
      this.revealTimer = window.setTimeout(this.endReveal, 400 + revealIndex * 90 + 600);
    }

    // An act that unlocks two towers is exactly when the shelf starts overflowing.
    this.syncOverflow();
  }

  private readonly endReturn = (): void => {
    this.root.classList.remove('is-returning');
  };

  private readonly endReveal = (): void => {
    for (const row of this.rows) {
      row.node.classList.remove('is-revealing');
      row.node.style.removeProperty('--reveal-i');
    }
  };

  private bind(row: Row): void {
    row.node.addEventListener('pointerdown', this.onPointerDown);
    row.node.addEventListener('click', this.onClick);
    row.node.addEventListener('pointerenter', this.onEnter);
    row.node.addEventListener('pointerleave', this.onLeave);
    row.node.addEventListener('focus', this.onEnter);
    row.node.addEventListener('blur', this.onLeave);
  }

  private unbind(row: Row): void {
    row.node.removeEventListener('pointerdown', this.onPointerDown);
    row.node.removeEventListener('click', this.onClick);
    row.node.removeEventListener('pointerenter', this.onEnter);
    row.node.removeEventListener('pointerleave', this.onLeave);
    row.node.removeEventListener('focus', this.onEnter);
    row.node.removeEventListener('blur', this.onLeave);
  }

  private rowFor(target: EventTarget | null): Row | null {
    if (!(target instanceof Element)) return null;
    const host = target.closest<HTMLElement>('.shop__item');
    if (!host) return null;
    return this.rows.find((r) => r.node === host) ?? null;
  }

  private affordable(row: Row): boolean {
    if (this.state.locked?.has(row.entry.id)) return false;
    return row.entry.cost <= this.state.cash;
  }

  private readonly onEnter = (ev: Event): void => {
    const row = this.rowFor(ev.currentTarget);
    if (row) this.cb.onPreview?.(row.entry);
  };

  private readonly onLeave = (): void => {
    this.cb.onPreview?.(null);
  };

  private readonly onClick = (ev: MouseEvent): void => {
    // A click that ended a drag has already placed the tower.
    if (this.dragArmed) { this.dragArmed = false; return; }

    const row = this.rowFor(ev.currentTarget);
    if (!row || !this.affordable(row)) return;
    const next = this.state.selected === row.entry.id ? null : row.entry.id;
    this.cb.onSelect?.(next);
  };

  private readonly onPointerDown = (ev: PointerEvent): void => {
    if (ev.button !== 0) return;
    const row = this.rowFor(ev.currentTarget);
    if (!row || !this.affordable(row)) return;
    this.dragging = row;
    this.dragArmed = false;
    this.dragPointer = ev.pointerId;
    this.dragFrom.x = ev.clientX;
    this.dragFrom.y = ev.clientY;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);
  };

  private readonly onPointerMove = (ev: PointerEvent): void => {
    const row = this.dragging;
    if (!row || ev.pointerId !== this.dragPointer) return;
    const dx = ev.clientX - this.dragFrom.x;
    const dy = ev.clientY - this.dragFrom.y;
    if (!this.dragArmed && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

    if (!this.dragArmed) {
      this.dragArmed = true;
      this.lift = ev.pointerType === 'mouse' ? 0 : TOUCH_LIFT_PX;
      /*
       * A wrapper, so the leader below the icon is drawn beside the mask rather
       * than through it. `.pxi` paints by masking a background colour, and a
       * pseudo-element on that box would be masked with it — the line would
       * simply not exist.
       */
      this.ghost = el('div', 'shop__ghost');
      if (this.lift > 0) {
        this.ghost.classList.add('is-lifted');
        this.ghost.style.setProperty('--ghost-lift', `${this.lift}px`);
      }
      this.ghost.append(iconFromId(row.entry.icon, FALLBACK_ICON, {
        size: 32,
        phosphor: row.entry.colour,
        glow: 1.2,
      }));
      document.body.append(this.ghost);
      this.cb.onSelect?.(row.entry.id);
    }
    const aimY = ev.clientY - this.lift;
    if (this.ghost) {
      this.ghost.style.left = `${ev.clientX}px`;
      this.ghost.style.top = `${aimY}px`;
    }
    this.cb.onDrag?.(row.entry.id, ev.clientX, aimY);
  };

  private readonly onPointerUp = (ev: PointerEvent): void => {
    const row = this.dragging;
    if (!row || ev.pointerId !== this.dragPointer) return;
    const wasDragging = this.dragArmed;
    // Read before `clearDrag`, which puts the lift back to zero: the tower has
    // to be built where the ghost was standing, not where the thumb was.
    const aimY = ev.clientY - this.lift;
    this.clearDrag();
    if (!wasDragging) return;
    /*
     * `dragArmed` stays up for exactly one task, and that is the fix.
     *
     * It exists to swallow the `click` a completed drag synthesises, so a
     * tower dropped on the board is not also armed by the tile it came out of.
     * As a plain latch it only ever cleared *inside* that click — and a drag
     * that ends over the canvas fires its click on the common ancestor of the
     * two targets, which is not the tile, so the listener never ran and the
     * latch stayed up for the rest of the session. The next press on any tile,
     * minutes later, was silently eaten: the shelf lit nothing, the cursor
     * armed nothing, and the player's build did not happen. Found by
     * `tools/uiprobe.mjs` only once the probe stopped clicking a tile between
     * the drag and the next build.
     *
     * A timeout of zero runs after the click that belongs to this gesture and
     * before anything the player can do next, which is the whole window the
     * latch is entitled to.
     */
    window.clearTimeout(this.dragArmedTimer);
    this.dragArmedTimer = window.setTimeout(this.dropDragArmed, 0);
    this.cb.onPlace?.(row.entry.id, ev.clientX, aimY);
  };

  private readonly dropDragArmed = (): void => { this.dragArmed = false; };

  private readonly onPointerCancel = (): void => {
    const armed = this.dragArmed;
    this.clearDrag();
    this.dragArmed = false;
    if (armed) this.cb.onSelect?.(null);
  };

  private clearDrag(): void {
    window.clearTimeout(this.dragArmedTimer);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
    this.ghost?.remove();
    this.ghost = null;
    this.dragging = null;
    this.dragPointer = -1;
    this.lift = 0;
  }
}
